import { BoxProps } from '@mui/material';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useTasksListQuery } from 'api';
import { Spinner } from 'components';
import { InstanceType, TaskStatus } from 'types';

import { EmptyState } from '../EmptyState';
import { TaskListItem } from '../TaskListItem';
import { ListW, MoreLink, TaskListW, Title } from './styled';

interface TasksListProps {
  title: string;
  status: TaskStatus;
  limit: number;
  assigned?: string;
  author?: string;
  isProfile?: boolean;
  listWrapperSx?: BoxProps['sx'];
  titleSx?: BoxProps['sx'];
  newTaskAssigneeId?: string;
  viewAllLink?: string;
}

export function TasksList({
  title,
  status,
  limit,
  assigned,
  author,
  listWrapperSx,
  titleSx,
  isProfile = false,
  newTaskAssigneeId,
  viewAllLink,
}: TasksListProps): ReactElement {
  const { t } = useTranslation();
  const { isLoading, tasks, total, lastItemRef, isFetchingNextPage } = useTasksListQuery({
    type: InstanceType.tasks,
    state: status,
    limit,
    ...(assigned && { assigned }),
    ...(author && { author }),
  });

  const moreCount = total - tasks.length;
  // let seeAllText = t('tasks.seeAllToDo');
  // if (status === TaskStatus.in_progress) {
  //   seeAllText = t('tasks.seeAllInProgress');
  // } else if (status === TaskStatus.to_approve) {
  //   seeAllText = t('tasks.seeAllApproval');
  // }

  return (
    <ListW sx={listWrapperSx}>
      <Title sx={titleSx}>{title}</Title>

      {isLoading && <Spinner size="1.5rem" />}

      {!isLoading && total === 0 ? (
        <TaskListW isProfile={isProfile}>
          <EmptyState
            status={status}
            assigneeId={newTaskAssigneeId}
            wrapperSx={{ border: '0px' }}
          />
        </TaskListW>
      ) : (
        <TaskListW isProfile={isProfile}>
          {tasks.length > 0 &&
            tasks.map(({ title, assigned_to, task, description, id, comments_count }, idx) => (
              <TaskListItem
                key={id}
                id={id}
                title={title}
                assignee={assigned_to}
                deadline={task.deadline}
                description={description}
                isProfile={isProfile}
                lastItemRef={idx === tasks.length - 1 ? lastItemRef : undefined}
                comments_count={comments_count}
                status={task.state}
                progress={task.progress}
                priority={task.priority}
              />
            ))}
          {viewAllLink && moreCount > 0 && (
            <MoreLink to={viewAllLink}>{t('tasks.xMore', { count: moreCount })}</MoreLink>
          )}
        </TaskListW>
      )}
      {isFetchingNextPage && <Spinner wrapperSx={{ bottom: '0', top: 'unset' }} />}
    </ListW>
  );
}
